import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';

const PERMS = [
  { key: 'pode_habilitar', label: 'Venda sem estoque' },
  { key: 'pode_consultar_estoque_disponivel', label: 'Estoque disponível' },
  { key: 'pode_consultar_estoque_bruto', label: 'Estoque bruto' },
  { key: 'pode_consultar_lote', label: 'Lote de pisos' },
  { key: 'pode_bloquear_devolucao', label: 'Bloquear devolução' },
  { key: 'autorizar_pedido', label: 'Aprovar pedidos MK' },
];

const EMPTY = {
  nome: '', numero: '',
  pode_habilitar: false,
  pode_consultar_estoque_disponivel: false,
  pode_consultar_estoque_bruto: false,
  pode_consultar_lote: false,
  pode_bloquear_devolucao: false,
  autorizar_pedido: false,
};

function formatNumero(numero) {
  const limpo = String(numero || '').replace(/\D/g, '');
  if (limpo.length === 11) return `(${limpo.slice(0, 2)}) ${limpo.slice(2, 7)}-${limpo.slice(7)}`;
  if (limpo.length === 10) return `(${limpo.slice(0, 2)}) ${limpo.slice(2, 6)}-${limpo.slice(6)}`;
  return numero;
}

export default function MartelinhoUsuarios() {
  const { temPermissao } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [modal, setModal] = useState(null); // { mode:'add'|'edit', data }
  const [salvando, setSalvando] = useState(false);

  const podeVer = temPermissao('martelinho:visualizar', 'martelinho:gerenciar');
  const podeGerir = temPermissao('martelinho:gerenciar');

  const carregar = () => {
    setLoading(true);
    axios.get(`${API}/martelinho/usuarios`)
      .then((res) => { setUsuarios(res.data?.data || []); setErro(''); })
      .catch(() => setErro('Não foi possível carregar os usuários.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!podeVer) { setLoading(false); return; }
    carregar();
  }, [podeVer]);

  const openAdd = () => setModal({ mode: 'add', data: { ...EMPTY } });
  const openEdit = (u) => setModal({ mode: 'edit', data: { ...u, autorizar_pedido: !!u.autorizar_pedido } });
  const closeModal = () => setModal(null);

  const handleChange = (key, value) => setModal((m) => ({ ...m, data: { ...m.data, [key]: value } }));

  const handleSave = async () => {
    setSalvando(true);
    const { mode, data } = modal;
    const url = mode === 'add'
      ? `${API}/martelinho/usuarios`
      : `${API}/martelinho/usuarios/${encodeURIComponent(String(data.numero).replace(/\D/g, ''))}`;
    try {
      await axios({ method: mode === 'add' ? 'post' : 'put', url, data });
      closeModal();
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro ao salvar', text: err.response?.data?.message || err.message });
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = async (numero) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Remover usuário?',
      text: `O usuário ${formatNumero(numero)} perderá o acesso ao bot.`,
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/martelinho/usuarios/${encodeURIComponent(String(numero).replace(/\D/g, ''))}`);
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro ao remover', text: err.response?.data?.message || err.message });
    }
  };

  const bool = (v) => (v
    ? <i className="bi bi-check-lg" style={{ color: '#4CB553' }} />
    : <span style={{ color: 'var(--portal-muted)' }}>—</span>);

  if (!podeVer) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">Você não tem permissão para ver os usuários do Martelinho.</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="mb-1" style={{ color: 'var(--portal-text)' }}>Usuários Autorizados</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Quem pode usar o bot e quais opções cada um enxerga.
          </p>
        </div>
        {podeGerir && (
          <button
            className="btn"
            onClick={openAdd}
            disabled={salvando}
            style={{ background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)', color: '#fff', fontWeight: 600, border: 'none', padding: '8px 18px' }}
          >
            <i className="bi bi-plus-lg me-2" /> Adicionar
          </button>
        )}
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      <div className="card p-0">
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" /></div>
        ) : usuarios.length === 0 ? (
          <div className="p-4 text-center text-muted">Nenhum usuário cadastrado.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle" style={{ color: 'var(--portal-text)' }}>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Número</th>
                  {PERMS.map((p) => <th key={p.key} className="text-center" style={{ fontSize: '0.78rem' }}>{p.label}</th>)}
                  {podeGerir && <th className="text-end">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.numero}>
                    <td>{u.nome}</td>
                    <td style={{ fontFamily: 'monospace' }}>{formatNumero(u.numero)}</td>
                    {PERMS.map((p) => <td key={p.key} className="text-center">{bool(u[p.key])}</td>)}
                    {podeGerir && (
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openEdit(u)}>Editar</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(u.numero)}>Excluir</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>
              <div className="modal-header" style={{ borderColor: 'var(--portal-border)' }}>
                <h5 className="modal-title">{modal.mode === 'add' ? 'Adicionar Usuário' : 'Editar Usuário'}</h5>
                <button className="btn-close btn-close-white" onClick={closeModal} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Nome</label>
                  <input className="form-control" value={modal.data.nome} onChange={(e) => handleChange('nome', e.target.value)} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Número (sem DDI, só dígitos)</label>
                  <input
                    className="form-control"
                    value={modal.data.numero}
                    onChange={(e) => handleChange('numero', e.target.value)}
                    disabled={modal.mode === 'edit'}
                    placeholder="ex: 44999001122"
                  />
                </div>
                <label className="form-label">Permissões</label>
                <div className="row g-2">
                  {PERMS.map((p) => (
                    <div key={p.key} className="col-12 col-sm-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`perm-${p.key}`}
                          checked={!!modal.data[p.key]}
                          onChange={(e) => handleChange(p.key, e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={`perm-${p.key}`}>{p.label}</label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer" style={{ borderColor: 'var(--portal-border)' }}>
                <button className="btn btn-outline-secondary" onClick={closeModal}>Cancelar</button>
                <button
                  className="btn"
                  onClick={handleSave}
                  disabled={salvando}
                  style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
                >
                  {salvando ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
